/*
 * Cart add-ons
 *
 * 1. Adds add-on products straight from the cart page (inline strip and popup).
 * 2. Blocks checkout while the cart is under the configured minimum order value
 *    and opens a popup asking the customer to top the cart up with add-ons.
 *
 * The inline strip lives inside the cart footer's `.js-contents`, so the theme
 * re-renders it (with a fresh cart total) on every cart change. All handlers are
 * therefore delegated from `document` rather than bound to the strip itself.
 */
(function () {
  'use strict';

  var CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    '.cart__checkout-button',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]'
  ].join(', ');

  var BODY_OPEN_CLASS = 'cart-addons-modal-open';
  var BELOW_MIN_CLASS = 'cart-below-minimum';

  function readState() {
    var el = document.querySelector('[data-cart-addons]');
    if (!el) return null;

    return {
      total: parseInt(el.dataset.cartTotal, 10) || 0,
      minimum: parseInt(el.dataset.minAmount, 10) || 0,
      enabled: el.dataset.minEnabled === 'true'
    };
  }

  function isBelowMinimum(state) {
    return !!state && state.enabled && state.minimum > 0 && state.total > 0 && state.total < state.minimum;
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.shopSettings) {
      return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function modal() {
    return document.querySelector('[data-cart-addons-modal]');
  }

  /* Sections the cart page needs re-rendered after an add-on is added.
     Mirrors CartItems.getSectionsToRender() in cart.js. */
  function sectionsToRender() {
    var miniCart = document.getElementById('mini-cart');
    var cartItems = document.getElementById('main-cart-items');
    var cartFooter = document.getElementById('main-cart-footer');

    return [
      { id: 'mini-cart', section: miniCart && miniCart.id, selector: '.shopify-section' },
      { id: 'main-cart-items', section: cartItems && cartItems.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: cartFooter && cartFooter.dataset.id, selector: '.js-contents' }
    ].filter(function (section) {
      return section.section;
    });
  }

  function sectionInnerHTML(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  function renderSections(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    sectionsToRender().forEach(function (section) {
      var element = document.getElementById(section.id);
      if (!element) return;

      var target = element.querySelector(section.selector) || element;
      if (target && parsedState.sections[section.section]) {
        target.innerHTML = sectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });
  }

  function addAddon(button) {
    if (button.disabled) return;

    var item = button.closest('.cart-addons__item');
    var select = item && item.querySelector('[data-cart-addon-variant]');
    var variantId = select ? select.value : button.dataset.variantId;
    if (!variantId) return;

    var originalLabel = button.textContent.trim();
    button.disabled = true;
    button.textContent = 'Adding...';

    var sections = sectionsToRender();
    var config = typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

    config.body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sections.map(function (section) {
        return section.section;
      }),
      sections_url: window.location.pathname
    });

    var addUrl = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add';

    fetch(addUrl, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (parsedState) {
        if (parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Cart error');
        }

        renderSections(parsedState);

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-addons' });
        }

        sync();
      })
      .catch(function (error) {
        button.disabled = false;
        button.textContent = originalLabel;

        var errors = document.getElementById('cart-errors');
        if (errors) {
          errors.textContent = error.message || ((window.theme && theme.cartStrings && theme.cartStrings.error) || '');
        }
      });
  }

  function fillModalMessage(state) {
    var el = modal();
    if (!el) return;

    var message = el.querySelector('[data-cart-addons-message]');
    if (!message) return;

    var remaining = Math.max(state.minimum - state.total, 0);
    message.innerHTML = (message.dataset.template || '')
      .replace(/\[amount\]/g, '<strong>' + formatMoney(remaining) + '</strong>')
      .replace(/\[minimum\]/g, '<strong>' + formatMoney(state.minimum) + '</strong>');
  }

  /* The popup shows the same cards as the inline strip, cloned so the two can
     never drift apart. */
  function fillModalBody() {
    var el = modal();
    if (!el) return;

    var body = el.querySelector('[data-cart-addons-body]');
    var list = document.querySelector('.cart-addons .cart-addons__list');
    if (!body) return;

    body.innerHTML = '';
    if (list) body.appendChild(list.cloneNode(true));
  }

  function openModal() {
    var el = modal();
    var state = readState();
    if (!el || !state) return;

    fillModalMessage(state);
    fillModalBody();

    el.hidden = false;
    document.body.classList.add(BODY_OPEN_CLASS);

    var focusTarget = el.querySelector('.cart-addons__add, .cart-addons-modal__close');
    if (focusTarget) focusTarget.focus();
  }

  function closeModal() {
    var el = modal();
    if (!el || el.hidden) return;

    el.hidden = true;
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  function isModalOpen() {
    var el = modal();
    return !!el && !el.hidden;
  }

  /* The delivery/pincode app injects its block into `.js-contents` after the
     tax note. Keep the strip directly under the subtotal so an app that
     re-inserts itself on a cart update cannot push it down the column. */
  function keepUnderSubtotal() {
    var strip = document.querySelector('.cart-addons');
    if (!strip) return;

    var taxNote = document.querySelector('#main-cart-footer .js-contents .tax-note');
    if (!taxNote || taxNote.parentElement !== strip.parentElement) return;
    if (strip.nextElementSibling === taxNote) return;

    taxNote.parentElement.insertBefore(strip, taxNote);
  }

  /* Reflect the current cart total: toggle the below-minimum styling and keep
     an open popup in step with the cart. */
  function sync() {
    keepUnderSubtotal();

    var state = readState();
    var below = isBelowMinimum(state);

    document.body.classList.toggle(BELOW_MIN_CLASS, below);

    if (!isModalOpen()) return;

    if (below) {
      fillModalMessage(state);
      fillModalBody();
    } else {
      closeModal();
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cart-addons-close]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    var addButton = target.closest('[data-cart-addon-add]');
    if (addButton) {
      event.preventDefault();
      addAddon(addButton);
    }
  });

  /* Capture phase so the gate runs before the theme's own checkout handlers. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var trigger = target.closest(CHECKOUT_SELECTOR);
      if (!trigger || !isBelowMinimum(readState())) return;

      event.preventDefault();
      event.stopPropagation();
      openModal();
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (!isBelowMinimum(readState())) return;

      event.preventDefault();
      event.stopPropagation();
      openModal();
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeModal();
  });

  document.addEventListener('cart:updated', sync);
  document.addEventListener('DOMContentLoaded', sync);

  if (document.readyState !== 'loading') sync();
})();
